import styled from '@emotion/styled';
import {Container, Spacer} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import * as motion from 'motion/react-m';

import {BlockIntro} from 'UI/components/layout/BlockIntro';
import {PayloadImage} from 'UI/components/layout/PayloadImage';
import {PayloadLexical} from 'UI/components/layout/PayloadLexical';
import {H4} from 'UI/components/layout/Text';
import {ViewportTrigger} from 'UI/components/layout/ViewportTrigger';

import {FadeInAnimation} from 'UI/animations/shared';

import type {ImageGridBlockType, ModuleIntro} from 'types/payload-types';

/**
 * The `ComponentProps` interface defines the properties for the `ImageGridBlock` component.
 * This interface specifies the structure and types of all properties required to render an image grid layout with accompanying content.
 * It ensures type safety and provides clear documentation for developers implementing image grid functionality within the application.
 */
interface ComponentProps {
    /**
     * Array of content items containing image data, titles, and optional descriptions for grid display.
     * Each item represents a single grid cell with its associated visual and textual content.
     */
    contents: ImageGridBlockType['contents'];
    /**
     * Unique identifier for the image grid block used for block intro functionality and component identification.
     * This identifier helps with content management and enables proper block-level configuration within the CMS.
     */
    id: string;
    /**
     * Module intro configuration object containing introductory content and settings for the block.
     * This object defines how the block introduction should be displayed above the image grid content.
     */
    intro: ModuleIntro;
    /**
     * The `testId` property represents a unique identifier for testing purposes.
     * This property is crucial for uniquely identifying components during testing procedures and automated quality assurance processes.
     *
     * @default 'ImageGridBlock'
     */
    testId?: string;
}

/**
 * The `ImageGridBlock` component displays a grid layout of images with titles and optional descriptions.
 * It renders images in a responsive grid format with accompanying text content for each item.
 * This component is commonly used to showcase portfolios, galleries, or product collections in a structured visual layout.
 *
 * @param props          The component props.
 * @param props.contents Array of content items containing image, title, and optional description data.
 * @param props.id       Unique identifier for the block, used for block intro functionality.
 * @param props.intro    Module intro configuration object containing introductory content for the block.
 * @param props.testId   Test identifier for component testing purposes.
 * @returns The rendered ImageGridBlock component JSX element.
 *
 * @example
 * ```tsx
 * <ImageGridBlock
 *     contents={[
 *         {
 *             id: '1',
 *             image: imageData,
 *             title: 'Sample Title',
 *             description: 'Sample description content'
 *         }
 *     ]}
 *     id="image-grid-1"
 *     intro={introConfig}
 *     testId="ImageGridBlock"
 * />
 * ```
 */
const ImageGridBlock = observer(({contents, id, intro, testId = 'ImageGridBlock'}: ComponentProps) => (
    <Container testId={testId}>
        <BlockIntro blockId={id} intro={intro} testId={`${testId}-Intro`} />
        <ViewportTrigger baseComponent={ImageGrid}>
            {contents.map(content => (
                <GridItem key={content.id ?? ''} variants={FadeInAnimation}>
                    <PayloadImage
                        expectedSizes={{lg: 226, md: 226, sm: 226, xl: 226, xs: 226, xxl: 226}}
                        fetchpriority="low"
                        image={content.image}
                        isLazy
                    />
                    <Spacer y={8} />
                    <MotionH4 as="h3" variants={FadeInAnimation}>{content.title}</MotionH4>
                    <Spacer y={4} />
                    <motion.div variants={FadeInAnimation}>
                        {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
                        {content.description && <PayloadLexical description={content.description} />}
                    </motion.div>
                </GridItem>
            ))}
        </ViewportTrigger>
    </Container>
));

ImageGridBlock.displayName = 'ImageGridBlock';

export {ImageGridBlock};

const ImageGrid = styled.div`
    column-gap: 80px;
    display: grid;
    grid-auto-rows: auto;
    grid-template-columns: repeat(auto-fit, minmax(150px, 226px));
    margin-inline: auto;
    max-width: 100%;
    width: fit-content;
`;

const GridItem = styled(motion.div)`
    display: grid;
    grid-row: span 5;
    grid-template-rows: subgrid;
`;

const MotionH4 = H4.withComponent(motion.create(H4));